import React from "react";
import { ChipGroup } from "@/components/ripeness/RadarFilterChips";
import { SavedViewsBar, hiddenConstraintCount } from "@/components/ripeness/SavedViewsBar";
import { FUNNEL_META } from "@/components/ripeness/radarMeta";

const SELECT_CLS = "rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100";

const SORT_OPTIONS = [
    ["ripeness:desc,last_evaluated_at:desc", "Ripeness (high → low)"],
    ["weighted_value:desc,ripeness:desc", "Value to us — weighted (high → low)"],
    ["expected_value:desc,ripeness:desc", "Expected value (high → low)"],
    ["value_if_awarded:desc,ripeness:desc", "Value if awarded (high → low)"],
    ["priority:desc,ripeness:desc", "Priority (high → low)"],
    ["last_signal_at:desc", "Latest AI signal"],
    ["last_evaluated_at:desc", "Recently evaluated"],
    ["next_reevaluation_due:asc", "Re-evaluation due soonest"],
    ["trial_count:desc", "Most matched trials"],
    ["mentions:desc", "Most AI mentions"],
    ["compound:asc", "Compound A → Z"],
];

function FacetSelect({ label, value, onChange, buckets, labelOf, testId, allLabel = "All" }) {
    return (
        <label className="text-xs text-slate-400 flex flex-col gap-1">
            {label}
            <select value={value} onChange={(e) => onChange(e.target.value)} className={SELECT_CLS} data-testid={testId}>
                <option value="">{allLabel}</option>
                {(buckets || []).map((r) => (
                    <option key={r.value} value={r.value}>{`${labelOf ? labelOf(r.value) : r.value} (${r.count})`}</option>
                ))}
            </select>
        </label>
    );
}

/** The Filters card: saved views, facet selects, sort, excluded toggle and
 *  the study-role / timing / CRO chip groups. `rf` is `useRipenessFilters()`. */
export function RipenessFilterPanel({ rf }) {
    const { filters, facets, setField, setChips, activeView, applyView, clearView, queryBody } = rf;
    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="radar-filter-panel">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Filters</div>
            <div className="space-y-3">
                <SavedViewsBar
                    currentBody={queryBody}
                    activeView={activeView}
                    onApply={applyView}
                    onClear={clearView}
                    hiddenCount={hiddenConstraintCount(filters.extra)}
                />
                <FacetSelect label="Tier" value={filters.tier} onChange={(v) => setField("tier", v)} buckets={facets?.tiers} testId="filter-tier-select" />
                <FacetSelect
                    label="Funnel stage" value={filters.funnel_stage} onChange={(v) => setField("funnel_stage", v)}
                    buckets={facets?.funnel_stages} labelOf={(v) => FUNNEL_META[v]?.label || v} testId="filter-funnel-select"
                />
                <FacetSelect label="Therapeutic area" value={filters.therapeutic_area} onChange={(v) => setField("therapeutic_area", v)} buckets={facets?.therapeutic_areas} testId="filter-area-select" />
                <label className="text-xs text-slate-400 flex flex-col gap-1">
                    Best phase
                    <select value={filters.best_phase} onChange={(e) => setField("best_phase", e.target.value)} className={SELECT_CLS} data-testid="filter-phase-select">
                        <option value="">Any</option>
                        {[1, 2, 3, 4].map((p) => <option key={p} value={String(p)}>{`P${p}`}</option>)}
                    </select>
                </label>
                <label className="text-xs text-slate-400 flex flex-col gap-1" title="Which phase the lead value on each row is computed for">
                    Value phase
                    <select value={filters.value_phase || "best"} onChange={(e) => setField("value_phase", e.target.value)} className={SELECT_CLS} data-testid="filter-value-phase-select">
                        <option value="best">{filters.best_phase ? `Follows filter (P${filters.best_phase})` : "Best phase of each track"}</option>
                        {[1, 2, 3, 4].map((p) => <option key={p} value={String(p)}>{`Value P${p} on every row`}</option>)}
                    </select>
                </label>
                <label className="text-xs text-slate-400 flex flex-col gap-1">
                    Sort
                    <select value={filters.sort} onChange={(e) => setField("sort", e.target.value)} className={SELECT_CLS} data-testid="filter-sort-select">
                        {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <label className="text-xs text-slate-400 flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={filters.include_excluded === "true"}
                        onChange={(e) => setField("include_excluded", e.target.checked ? "true" : "")}
                        data-testid="filter-show-excluded-check"
                    />
                    Show excluded tracks{facets?.excluded_tracks ? ` (${facets.excluded_tracks})` : ""}
                </label>
                <div className="border-t border-slate-800 pt-3 space-y-3" data-testid="radar-filter-chips">
                    <ChipGroup family="study_role" title="Study role" buckets={facets?.study_roles} selected={filters.study_role} onChange={setChips("study_role")} />
                    <ChipGroup family="timing" title="Timing" buckets={facets?.timing} selected={filters.timing} onChange={setChips("timing")} />
                    <ChipGroup
                        family="cro_status" title="CRO status" buckets={facets?.cro_status} selected={filters.cro_status} onChange={setChips("cro_status")}
                        hint="Counts show what each chip would add to the current selection."
                    />
                </div>
            </div>
        </div>
    );
}
